import React, { useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { RotateCcw, XCircle } from "lucide-react";
import { formatMoney, humanize } from "../orderViewUtils";
import { getSafeUserMessage } from "../../../utils/errorMessages";

export default function OrderFinancialDialog({ action, amount: maximum, currency, provider, textile = false, refundRequired = false, onClose, onConfirm }) {
  const [amount, setAmount] = useState(maximum);
  const [reason, setReason] = useState("");
  const [inventory, setInventory] = useState("");
  const [override, setOverride] = useState(false);
  const [paymentAction, setPaymentAction] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);
  const idempotencyKey = useRef(`admin-${action}-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const cancellation = action === "cancel";
  const paymentValue = cancellation && !refundRequired ? "cancel_unpaid" : "refund";

  async function submit(event) {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      await onConfirm({ amount: Number(amount), reason: reason.trim(), restoreInventory: inventory === "restore",
        textileOverride: override, paymentAction, idempotencyKey: idempotencyKey.current });
      onClose();
    } catch (err) {
      setError(getSafeUserMessage(err.message, "The provider action could not be confirmed. Refresh before retrying."));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  return (
    <Modal show onHide={() => { if (!busy) onClose(); }} centered backdrop={busy ? "static" : true} keyboard={!busy}>
      <Form onSubmit={submit}>
        <Modal.Header closeButton={!busy}><Modal.Title>{textile ? "Exceptional Textile override: " : ""}{cancellation ? "Cancel order" : "Refund"}</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>Payment provider: {humanize(provider, "No provider payment")}</p>
          {cancellation ? <p>Cancellation cannot be undone. {refundRequired ? `The remaining ${formatMoney(maximum, currency)} will be refunded through the provider.` : "The unpaid payment attempt will be cancelled."}</p> : (
            <Form.Group className="mb-3" controlId="financial-amount"><Form.Label>Refund amount ({currency})</Form.Label>
              <Form.Control type="number" step="0.01" min="0.01" max={maximum} required disabled={busy} value={amount} onChange={(event) => setAmount(event.target.value)} />
              <Form.Text>Available: {formatMoney(maximum, currency)}</Form.Text>
            </Form.Group>
          )}
          <Form.Group className="mb-3" controlId="financial-reason"><Form.Label>Reason</Form.Label>
            <Form.Control as="textarea" rows={3} maxLength={1000} required disabled={busy} value={reason} onChange={(event) => setReason(event.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3" controlId="financial-payment"><Form.Label>Payment action</Form.Label>
            <Form.Select required disabled={busy} value={paymentAction} onChange={(event) => setPaymentAction(event.target.value)}>
              <option value="">Select payment action</option>
              <option value={paymentValue}>{paymentValue === "refund" ? `Refund ${formatMoney(amount, currency)} through ${humanize(provider)}` : "Cancel unpaid payment"}</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3" controlId="financial-inventory"><Form.Label>Inventory after confirmed refund</Form.Label>
            <Form.Select required disabled={busy} value={inventory} onChange={(event) => setInventory(event.target.value)}>
              <option value="">Select inventory handling</option><option value="keep">Leave stock unchanged</option><option value="restore">Restore eligible stock</option>
            </Form.Select>
            {cancellation && !refundRequired ? <Form.Text>Any unpaid stock holds are released when cancellation succeeds.</Form.Text> : <Form.Text>Partial quantities need manual stock review.</Form.Text>}
          </Form.Group>
          {textile ? <Form.Check id="financial-textile-override" type="checkbox" required disabled={busy} checked={override}
            onChange={(event) => setOverride(event.target.checked)} label="Confirm this exceptional admin Textile override" /> : null}
          {error ? <p className="mt-3" role="alert">{error}</p> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={busy} onClick={onClose}>Back</Button>
          <Button type="submit" variant="danger" disabled={busy || !reason.trim() || !inventory || !paymentAction || (textile && !override)}>
            {cancellation ? <XCircle size={16} /> : <RotateCcw size={16} />} {busy ? "Submitting..." : cancellation ? "Confirm cancellation" : `Confirm refund ${formatMoney(amount, currency)}`}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
